import { Fragment, useEffect, useState } from 'react'
import { Link, useLocation } from '@tanstack/react-router'
import {
  Home,
  ChartColumn,
  FolderKanban,
  ShoppingCart,
  FileStack,
  Truck,
  Landmark,
  Users,
  Package,
  ShieldCheck,
  Archive,
  FlaskConical,
  ChevronRight,
  GraduationCap,
  Warehouse,
  type LucideIcon,
} from 'lucide-react'
import { cn } from '@/lib/utils'
import { LogoIcon } from '@/assets/Logo'
import { Sheet, SheetContent, SheetTitle } from '@/components/ui/sheet'
import { useChatsWaitingCount } from '@/features/storefront-chat/alerts-store'
import { usePoReviewWaitingCount } from '@/features/quote-builder/project/api'

// ``search`` links to a filtered view of a page, e.g. Action Items' Data role (USA-1655).
type NavItem = { label: string; href: string; search?: Record<string, string> }
type NavGroup = { key: string; label: string; icon: LucideIcon; items: NavItem[] }

const CHATS_HREF = '/customer-comms/chats'

// Customer Comms (/customer-comms) is off the sidebar for now (USA-1277); its
// threads stay reachable from projects and POs.
const navGroups: NavGroup[] = [
  {
    key: 'quotes',
    label: 'Quotes',
    icon: FileStack,
    // Quote Builder (USA-1015). Owners append their one line; never reorder.
    items: [
      { label: 'My Quotes', href: '/quotes' },
      { label: 'New Quote', href: '/quotes/new' },
      { label: 'Email', href: '/communications' },
    ],
  },
  {
    key: 'projects',
    label: 'Projects',
    icon: FolderKanban,
    items: [
      { label: 'All Projects', href: '/projects' },
      { label: 'Action Items', href: '/projects/action-items' },
      { label: 'Waiting On', href: '/projects/waiting-on' },
      { label: 'Timeline', href: '/projects/timeline' },
      { label: 'Sales Overview', href: '/projects/sales-overview' },
    ],
  },
  {
    key: 'orders',
    label: 'Orders',
    icon: ShoppingCart,
    items: [
      { label: 'Sales Orders', href: '/orders' },
      { label: 'Purchase Orders', href: '/purchase-orders' },
      { label: 'Quoted vs Actual', href: '/quoted-vs-actual' },
      { label: 'Order Review', href: '/order-review' },
    ],
  },
  {
    key: 'shipping',
    label: 'Shipping',
    icon: Truck,
    items: [
      { label: 'Shipments', href: '/shipments' },
      { label: 'Calculator', href: '/shipping-calculator' },
    ],
  },
  {
    key: 'warehouse',
    label: 'Warehouse',
    icon: Warehouse,
    items: [
      { label: 'Stock', href: '/warehouse' },
      { label: 'Locations', href: '/warehouse/locations' },
    ],
  },
  {
    key: 'accounting',
    label: 'Accounting',
    icon: Landmark,
    items: [
      { label: 'AP Invoices', href: '/invoices' },
      { label: 'AR Invoices', href: '/receivables' },
      { label: 'Tax Rates', href: '/tax-rates' },
      { label: 'Tax Exemptions', href: '/tax-exemptions' },
    ],
  },
  {
    key: 'customers',
    label: 'Customers',
    icon: Users,
    items: [
      { label: 'All Customers', href: '/customers' },
      { label: 'Warranty Claims', href: '/warranty-claims' },
      { label: 'Chats', href: CHATS_HREF },
      { label: 'Store AI', href: '/customer-comms/store-ai' },
    ],
  },
  {
    key: 'catalog',
    label: 'Catalog',
    icon: Package,
    items: [
      { label: 'Products', href: '/products' },
      { label: 'Search', href: '/catalog-search' },
      { label: 'Manufacturers', href: '/manufacturers' },
      // The data agent's and order review's items: the Action Items page on its Data role (USA-1655).
      { label: 'Data Items', href: '/projects/action-items', search: { role: 'data' } },
      { label: 'Store readiness', href: '/store-readiness' },
      { label: 'Suppliers', href: '/suppliers' },
      { label: 'Buying sources', href: '/sources' },
      { label: 'Quality Tiers', href: '/quality-tiers' },
      { label: 'Specials', href: '/specials' },
      { label: 'Price Matching', href: '/price-matching' },
      { label: 'Sources', href: '/catalog-sources' },
    ],
  },
  {
    key: 'admin',
    label: 'Admin',
    icon: ShieldCheck,
    items: [
      { label: 'Staff', href: '/staff' },
      { label: 'AI Agents', href: '/ai' },
      { label: 'AI Costs', href: '/ai-costs' },
      { label: 'API Keys', href: '/ai/api-keys' },
      { label: 'Company', href: '/company' },
    ],
  },
  {
    key: 'legacy',
    label: 'Legacy',
    icon: Archive,
    // Kept available per USA-1034.
    items: [{ label: 'Quote Drafts', href: '/projects/dropship-drafts' }],
  },
]

const allNavItems: NavItem[] = [
  { label: 'Dashboard', href: '/dashboard' },
  { label: 'Analytics', href: '/analytics' },
  { label: 'Learn', href: '/learn' },
  ...navGroups.flatMap((g) => g.items),
]

function isNavItemActive(pathname: string, href: string) {
  return (
    pathname === href ||
    (href !== '/dashboard' &&
      href !== '/projects/timeline' &&
      pathname.startsWith(href) &&
      !allNavItems.some(
        (other) => other.href !== href && other.href.startsWith(href) && pathname.startsWith(other.href),
      ))
  )
}

type NavSearch = Record<string, unknown>

function matchesSearch(item: NavItem, search: NavSearch) {
  return Object.entries(item.search ?? {}).every(([key, value]) => search[key] === value)
}

/** A filtered item is active on its exact view; the plain page item is active everywhere else on it. */
function isItemActive(pathname: string, search: NavSearch, item: NavItem): boolean {
  if (item.search) return pathname === item.href && matchesSearch(item, search)
  return (
    isNavItemActive(pathname, item.href) &&
    !allNavItems.some((other) => other.search && other.href === item.href && isItemActive(pathname, search, other))
  )
}

function activeGroupKey(pathname: string, search: NavSearch) {
  return navGroups.find((group) => group.items.some((item) => isItemActive(pathname, search, item)))?.key
}

const OPEN_GROUPS_KEY = 'usars.sidebar.open-groups'

function readOpenGroups(): string[] {
  try {
    const stored: unknown = JSON.parse(localStorage.getItem(OPEN_GROUPS_KEY) ?? '[]')
    return Array.isArray(stored) ? stored.filter((key): key is string => typeof key === 'string') : []
  } catch {
    return []
  }
}

const rowClass = 'flex w-full items-center gap-3 rounded-md px-3 text-sm font-medium transition-colors'
const idleClass = 'text-sidebar-foreground/60 hover:bg-sidebar-accent/50 hover:text-sidebar-accent-foreground'
const activeClass = 'bg-sidebar-accent text-sidebar-accent-foreground'

function WaitingBadge({ count }: { count: number }) {
  if (count <= 0) return null
  return (
    <span
      aria-label={`${count} waiting`}
      className="rounded-full bg-destructive px-1.5 text-[11px] font-semibold tabular-nums text-white"
    >
      {count}
    </span>
  )
}

function NavGroupSection({
  group,
  pathname,
  search,
  open,
  onToggle,
  waiting,
}: {
  group: NavGroup
  pathname: string
  search: NavSearch
  open: boolean
  onToggle: () => void
  /** What waits on the signed-in user, by page: shopper chats, and projects for PO review (USA-1335). */
  waiting: Record<string, number>
}) {
  const containsActive = group.items.some((item) => isItemActive(pathname, search, item))
  const groupWaiting = group.items.reduce((sum, item) => sum + (waiting[item.href] ?? 0), 0)
  const listId = `sidebar-group-${group.key}`

  return (
    <div>
      <button
        type="button"
        aria-expanded={open}
        aria-controls={listId}
        onClick={onToggle}
        className={cn(rowClass, 'py-2.5', containsActive && !open ? activeClass : idleClass)}
      >
        <group.icon className="h-[18px] w-[18px] shrink-0" />
        {group.label}
        <span className="ml-auto flex items-center gap-1.5">
          {!open && <WaitingBadge count={groupWaiting} />}
          <ChevronRight
            aria-hidden="true"
            className={cn(
              'h-3.5 w-3.5 shrink-0 opacity-50 transition-transform motion-reduce:transition-none',
              open && 'rotate-90',
            )}
          />
        </span>
      </button>
      {open && (
        <ul id={listId} className="mb-1 ml-[21px] space-y-0.5 border-l border-sidebar-border pl-2">
          {group.items.map((item) => {
            const active = isItemActive(pathname, search, item)
            return (
              <li key={item.label}>
                <Link
                  to={item.href}
                  search={item.search}
                  activeOptions={{ exact: true }}
                  aria-current={active ? 'page' : undefined}
                  className={cn(rowClass, 'py-2', active ? activeClass : idleClass)}
                >
                  {item.label}
                  {waiting[item.href] ? (
                    <span className="ml-auto">
                      <WaitingBadge count={waiting[item.href]!} />
                    </span>
                  ) : null}
                </Link>
              </li>
            )
          })}
        </ul>
      )}
    </div>
  )
}

const testingItems = [{ label: 'E2E Testing', href: '/e2e-testing', icon: FlaskConical }]

const isDev = import.meta.env.DEV

function SidebarNav() {
  const { pathname, search } = useLocation()
  const chatsWaiting = useChatsWaitingCount()
  const poReviewsWaiting = usePoReviewWaitingCount()
  const waiting = { [CHATS_HREF]: chatsWaiting, '/projects': poReviewsWaiting }
  const [openGroups, setOpenGroups] = useState<string[]>(() => {
    const active = activeGroupKey(pathname, search)
    const stored = readOpenGroups()
    return active && !stored.includes(active) ? [...stored, active] : stored
  })

  // Navigating into a collapsed section opens it, so the current page is always visible.
  // The Data role filter is a page of its own in the sidebar (USA-1655), so it counts as navigation too.
  const locationKey = `${pathname}?${String((search as NavSearch).role ?? '')}`
  const [seenLocation, setSeenLocation] = useState(locationKey)
  if (seenLocation !== locationKey) {
    setSeenLocation(locationKey)
    const active = activeGroupKey(pathname, search)
    if (active && !openGroups.includes(active)) setOpenGroups([...openGroups, active])
  }

  useEffect(() => {
    try {
      localStorage.setItem(OPEN_GROUPS_KEY, JSON.stringify(openGroups))
    } catch {
      // Storage unavailable (private mode): sections still toggle for this visit.
    }
  }, [openGroups])

  const toggle = (key: string) =>
    setOpenGroups((current) => (current.includes(key) ? current.filter((k) => k !== key) : [...current, key]))

  const dashboardActive = isNavItemActive(pathname, '/dashboard')
  const analyticsActive = isNavItemActive(pathname, '/analytics')
  const learnActive = isNavItemActive(pathname, '/learn')

  return (
    <>
      <div className="flex items-center px-5 py-5">
        <Link to="/dashboard" aria-label="USA Restaurant Suppliers dashboard">
          <LogoIcon />
        </Link>
      </div>

      <nav className="flex flex-1 flex-col space-y-0.5 overflow-y-auto px-3 pb-4">
        <Link
          to="/dashboard"
          activeOptions={{ exact: true }}
          aria-current={dashboardActive ? 'page' : undefined}
          className={cn(rowClass, 'py-2.5', dashboardActive ? activeClass : idleClass)}
        >
          <Home className="h-[18px] w-[18px] shrink-0" />
          Dashboard
        </Link>
        <Link
          to="/analytics"
          aria-current={analyticsActive ? 'page' : undefined}
          className={cn(rowClass, 'py-2.5', analyticsActive ? activeClass : idleClass)}
        >
          <ChartColumn className="h-[18px] w-[18px] shrink-0" />
          Analytics
        </Link>
        {navGroups.map((group) => (
          <Fragment key={group.key}>
            {/* Learn sits between Catalog and Admin (USA-1631). */}
            {group.key === 'admin' && (
              <Link
                to="/learn"
                aria-current={learnActive ? 'page' : undefined}
                className={cn(rowClass, 'py-2.5', learnActive ? activeClass : idleClass)}
              >
                <GraduationCap className="h-[18px] w-[18px] shrink-0" />
                Learn
              </Link>
            )}
            <NavGroupSection
              group={group}
              pathname={pathname}
              search={search}
              open={openGroups.includes(group.key)}
              onToggle={() => toggle(group.key)}
              waiting={waiting}
            />
          </Fragment>
        ))}

        {isDev && (
          <div className="mt-auto pt-4">
            <div className="mb-1 px-3 text-xs font-semibold uppercase tracking-wider text-sidebar-foreground/60">
              Testing
            </div>
            {testingItems.map((item) => {
              const isActive = pathname.startsWith(item.href)
              return (
                <Link
                  key={item.href}
                  to={item.href}
                  className={cn(rowClass, 'py-2.5', isActive ? activeClass : idleClass)}
                >
                  <item.icon className="h-[18px] w-[18px]" />
                  {item.label}
                </Link>
              )
            })}
          </div>
        )}
      </nav>
    </>
  )
}

type SidebarProps = {
  open: boolean
  onOpenChange: (open: boolean) => void
}

export function Sidebar({ open, onOpenChange }: SidebarProps) {
  const location = useLocation()

  // Auto-close mobile sidebar on navigation
  useEffect(() => {
    onOpenChange(false)
  }, [location.pathname, onOpenChange])

  return (
    <>
      {/* Desktop sidebar */}
      <aside className="hidden h-full w-[200px] shrink-0 flex-col border-r border-sidebar-border bg-sidebar md:flex">
        <SidebarNav />
      </aside>

      {/* Mobile sidebar drawer */}
      <Sheet open={open} onOpenChange={onOpenChange}>
        <SheetContent side="left" className="w-[240px] bg-sidebar p-0 pt-[env(safe-area-inset-top)] pb-[env(safe-area-inset-bottom)]">
          <SheetTitle className="sr-only">Navigation</SheetTitle>
          <div className="flex h-full flex-col">
            <SidebarNav />
          </div>
        </SheetContent>
      </Sheet>
    </>
  )
}
